import { FC, useContext, useMemo } from "react";
import { ZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext";
import { ZipClassTableData, ZipProjectActionsEnum } from "@app/projects/zip-projects/interfaces";
import { ColumnConfig, ColumnGroup, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { Link, useLocation, useRouteMatch } from "react-router-dom";
import { RegionGranularity } from "@app/region-sets";
import { NetworkAssessmentAnalysisType, ProjectPerspective } from "@app/projects/enumerators";
import { ExportType } from "@app/exports/enumerations";
import { blankZipClassRow, fillInMissingCompareModeRows, getTopAndBottomRunConfigurations, shouldUseComparedZipDetails, printNumericString, printPercentString, getResultsForLabel } from "@app/projects/zip-projects/utility";
import InformationalIndicator from "@app/projects/network-assessment/InformationalSpecialtyGroupIndicator";
import { CalculationMethodEnum } from "@app/templates/templateModels";
import { getPrimaryAndSecondaryRunConfigurations, getMarketAnalysisTooltip } from "@app/projects/network-assessment/adequacy/utility";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ReportCustomizationRouteState } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { ZipClassAggregateResultDisplayModel } from "@app/projects/zip-projects/interfaces/zipClassAggregateResultDisplayModel";
import { useZipProjectPaginationProps } from "./UseZipProjectPaginationProps";
import { formatDateTime } from "@app/shared/formatDateTime";

interface RouteMatchParams {
  projectId: string;
}

export const ZipClassTable: FC = () => {
  const { state, dispatch, ...projectContext } = useContext(ZipProjectContext);
  const location = useLocation();
  const routeMatch = useRouteMatch<RouteMatchParams>();
  const { calculatedTotalNumberOfRecords, paginationProps } = useZipProjectPaginationProps();
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const displayMarketAnalysisResults = (enabledFeatureFlags.has(Features.ZIP_PROSPECT_AND_MARKET)
    && (state.runConfiguration.useNetworkProvidersForMarketAnalysis || state.runConfiguration.useProspectProvidersForMarketAnalysis || !!state.runConfiguration.marketProviderSetId));

  const handleCheckboxChange = (wafflesSelectedKeys: string[]) => {
    dispatch({
      payload: {
        selectedRows: wafflesSelectedKeys,
      },
      type: ZipProjectActionsEnum.ChangeCheckbox,
    });
  }

  const columnConfig: ColumnConfig = useMemo(() => {
    const ncpCountTwo = state.templateMetadata?.closestProviderCount === 2 || (state.isCompareMode && state.templateMetadataCompared?.closestProviderCount === 2);
    const ncpCountGtOne = state.templateMetadata?.closestProviderCount > 1 || (state.isCompareMode && state.templateMetadataCompared?.closestProviderCount > 1);
    const isTimeAndDistance = state.calculationMethod?.toLocaleLowerCase() === CalculationMethodEnum.TimeAndDistance.toLocaleLowerCase();
    const isTimeOrDistance = state.calculationMethod?.toLocaleLowerCase() === CalculationMethodEnum.TimeOrDistance.toLocaleLowerCase();
    const showDist = state.runConfiguration?.showDistance || (state.isCompareMode && state.compareToRunConfiguration?.showDistance);
    const showTime = state.runConfiguration?.showTime || (state.isCompareMode && state.compareToRunConfiguration?.showTime);
    const showAvgDist = state.project?.showAverageDistance && showDist;
    const showAvgTime = state.project?.showAverageTime && showTime;
    const showMaxDist = state.project?.showMaximumDistance && showDist;
    const showMaxTime = state.project?.showMaximumTime && showTime;
    const showWithTimeAndDistanceColumns = state.runConfiguration?.showWithTimeAndDistanceColumns || (state.isCompareMode && state.compareToRunConfiguration?.showWithTimeAndDistanceColumns);

    return {
      accessTelehealthCredit: {
        displayText: "Telehealth %",
        sortOrder: 395,
        testId: "telehealth-credit",
      },
      avgDist: {
        displayText: ncpCountGtOne ? "Avg Dist 1" : "Avg Dist",
        hidden: !showAvgDist,
        sortOrder: 170,
        testId: "avg-dist",
      },
      avgDist2: {
        displayText: "Avg Dist 2",
        hidden: !ncpCountTwo || !showAvgDist,
        sortOrder: 290,
        testId: "avg-dist-2",
      },
      avgDistWith: {
        displayText: ncpCountGtOne ? "Avg Dist 1 With Access" : "Avg Dist With Access",
        hidden: !showAvgDist,
        sortOrder: 180,
        testId: "avg-dist-with",
      },
      avgDistWith2: {
        displayText: "Avg Dist 2 With Access",
        hidden: !ncpCountTwo || !showAvgDist,
        sortOrder: 300,
        testId: "avg-dist-2-with",
      },
      avgDistWithout: {
        displayText: ncpCountGtOne ? "Avg Dist 1 Without Access" : "Avg Dist Without Access",
        hidden: !showAvgDist,
        sortOrder: 190,
        testId: "avg-dist-without",
      },
      avgDistWithout2: {
        displayText: "Avg Dist 2 Without Access",
        hidden: !ncpCountTwo || !showAvgDist,
        sortOrder: 310,
        testId: "avg-dist-2-without",
      },
      avgTime: {
        displayText: ncpCountGtOne ? "Avg Time 1" : "Avg Time",
        hidden: !showAvgTime,
        sortOrder: 230,
        testId: "avg-time",
      },
      avgTime2: {
        displayText: "Avg Time 2",
        hidden: !ncpCountTwo || !showAvgTime,
        sortOrder: 360,
        testId: "avg-time-2",
      },
      avgTimeWith: {
        displayText: ncpCountGtOne ? "Avg Time 1 With Access" : "Avg Time With Access",
        hidden: !showAvgTime,
        sortOrder: 240,
        testId: "avg-time-with",
      },
      avgTimeWith2: {
        displayText: "Avg Time 2 With Access",
        hidden: !ncpCountTwo || !showAvgTime,
        sortOrder: 370,
        testId: "avg-time-2-with",
      },
      avgTimeWithout: {
        displayText: ncpCountGtOne ? "Avg Time 1 Without Access" : "Avg Time Without Access",
        hidden: !showAvgTime,
        sortOrder: 250,
        testId: "avg-time-without",
      },
      avgTimeWithout2: {
        displayText: "Avg Time 2 Without Access",
        hidden: !ncpCountTwo || !showAvgTime,
        sortOrder: 380,
        testId: "avg-time-2-without",
      },
      exceptions: {
        displayText: "Exceptions",
        hidden: !enabledFeatureFlags.has(Features.ZIP_EXCEPTIONS),
        sortOrder: 460,
        testId: "exceptions",
      },
      inClassMarketProviderCount: {
        displayText: "Market In Classification",
        hidden: !displayMarketAnalysisResults,
        sortOrder: 441,
        testId: "in-class-market-provider-count"
      },
      inClassProviderCount: {
        displayText: "In Classification Providers",
        sortOrder: 421,
        testId: "in-class-provider-count"
      },
      isInformationalSpecialty: {
        displayText: "Specialty is Informational",
        hidden: true,
        testId: "is-informational-specialty",
      },
      marketAccessPercent: {
        displayText: "Access %",
        hidden: !displayMarketAnalysisResults,
        sortOrder: 430,
        testId: "market-access-percent",
      },
      marketPercent: {
        displayText: "Market %",
        hidden: !displayMarketAnalysisResults,
        sortOrder: 450,
        testId: "market-percent",
      },
      marketServicing: {
        displayText: "Servicing",
        hidden: !displayMarketAnalysisResults,
        sortOrder: 440,
        testId: "market-servicing",
      },
      maxDist: {
        displayText: ncpCountGtOne ? "Max Dist 1" : "Max Dist",
        hidden: !showMaxDist,
        sortOrder: 200,
        testId: "max-dist",
      },
      maxDist2: {
        displayText: "Max Dist 2",
        hidden: !ncpCountTwo || !showMaxDist,
        sortOrder: 330,
        testId: "max-dist-2",
      },
      maxDistWith: {
        displayText: ncpCountGtOne ? "Max Dist 1 With Access" : "Max Dist With Access",
        hidden: !showMaxDist,
        sortOrder: 210,
        testId: "max-dist-with",
      },
      maxDistWith2: {
        displayText: "Max Dist 2 With Access",
        hidden: !ncpCountTwo || !showMaxDist,
        sortOrder: 340,
        testId: "max-dist-2-with",
      },
      maxDistWithout: {
        displayText: ncpCountGtOne ? "Max Dist 1 Without Access" : "Max Dist Without Access",
        hidden: !showMaxDist,
        sortOrder: 220,
        testId: "max-dist-without",
      },
      maxDistWithout2: {
        displayText: "Max Dist 2 Without Access",
        hidden: !ncpCountTwo || !showMaxDist,
        sortOrder: 350,
        testId: "max-dist-2-without",
      },
      maxTime: {
        displayText: ncpCountGtOne ? "Max Time 1" : "Max Time",
        hidden: !showMaxTime,
        sortOrder: 260,
        testId: "max-time",
      },
      maxTime2: {
        displayText: "Max Time 2",
        hidden: !ncpCountTwo || !showMaxTime,
        sortOrder: 390,
        testId: "max-time-2",
      },
      maxTimeWith: {
        displayText: ncpCountGtOne ? "Max Time 1 With Access" : "Max Time With Access",
        hidden: !showMaxTime,
        sortOrder: 270,
        testId: "max-time-with",
      },
      maxTimeWith2: {
        displayText: "Max Time 2 With Access",
        hidden: !ncpCountTwo || !showMaxTime,
        sortOrder: 400,
        testId: "max-time-2-with",
      },
      maxTimeWithout: {
        displayText: ncpCountGtOne ? "Max Time 1 Without Access" : "Max Time Without Access",
        hidden: !showMaxTime,
        sortOrder: 280,
        testId: "max-time-without",
      },
      maxTimeWithout2: {
        displayText: "Max Time 2 Without Access",
        hidden: !ncpCountTwo || !showMaxTime,
        sortOrder: 410,
        testId: "max-time-2-without",
      },
      memberCountWith: {
        displayText: "# With",
        sortOrder: 130,
        testId: "member-count-with",
      },
      memberCountWithout: {
        displayText: "# Without",
        sortOrder: 150,
        testId: "member-count-without",
      },
      membershipCount: {
        displayText: "Membership Count",
        sortOrder: 31,
        testId: "membership-count",
      },
      membershipType: {
        displayText: "Membership",
        noSort: true,
        sortOrder: 30,
        testId: "membership",
      },
      metDist: {
        displayText: "Met Dist",
        hidden: !showWithTimeAndDistanceColumns || isTimeAndDistance,
        sortOrder: 90,
        testId: "met-dist",
      },
      metOverall: {
        displayText: "Met Overall",
        sortOrder: 110,
        testId: "met-overall",
      },
      metTime: {
        displayText: "Met Time",
        hidden: !showWithTimeAndDistanceColumns || isTimeAndDistance,
        sortOrder: 100,
        testId: "met-time",
      },
      minPassPercent: {
        displayText: "Min Pass %",
        sortOrder: 70,
        testId: "min-pass-percent",
      },
      minProviderCount: {
        displayText: "Min Reqd Provs",
        sortOrder: 40,
        testId: "min-provider-count",
      },
      providerCount: {
        displayText: "Servicing Providers",
        sortOrder: 420,
        testId: "provider-count",
      },
      reqDist: {
        displayText: "Reqd Dist",
        hidden: !showDist,
        sortOrder: 50,
        testId: "req-dist",
      },
      reqTime: {
        displayText: "Reqd Time",
        hidden: !showTime,
        sortOrder: 60,
        testId: "req-time",
      },
      resultsFor: {
        displayText: "Results For",
        hidden: !state.isCompareMode,
        noSort: true,
        sortOrder: 25,
        testId: "results-for",
      },
      servicingProviderToMemberRatio: {
        displayText: "Svcg Prov To Mbr Ratio",
        sortOrder: 421,
        testId: "servicing-provider-to-member-ratio",
      },
      specialty: {
        displayText: "Specialty Group",
        sortOrder: 20,
        testId: "specialty",
      },
      withDistPercent: {
        displayText: "With Dist %",
        hidden: !isTimeOrDistance,
        sortOrder: ncpCountTwo ? 355 : 225,
        testId: "with-dist-percent",
      },
      withoutPercent: {
        displayText: "Without %",
        sortOrder: 160,
        testId: "without-percent",
      },
      withPercent: {
        displayText: "With %",
        sortOrder: 140,
        testId: "with-percent",
      },
      withTimePercent: {
        displayText: "With Time %",
        hidden: !isTimeOrDistance,
        sortOrder: ncpCountTwo ? 415 : 285,
        testId: "with-time-percent",
      },
      zipClass: {
        displayText: "Classification",
        sortOrder: 10,
        testId: "classification",
      },
    };
  }, [
    state.project?.showAverageDistance,
    state.project?.showAverageTime,
    state.project?.showMaximumDistance,
    state.project?.showMaximumTime,
    state.runConfiguration?.showDistance,
    state.runConfiguration?.showTime,
    state.templateMetadata?.closestProviderCount,
    state.isCompareMode,
    state.calculationMethod,
    state.runConfiguration
  ]);

  const bulkActionBar = useMemo(() => {
    if (!state.runConfiguration) {
      return null;
    }

    const regionSetIds: number[] = [];
    const specialtyGroupCodes: string[] = [];
    state.selectedRows.forEach((sr) => {
      regionSetIds.push(state.regionSetSpecialtyGroupToPartsLookup[sr].regionSetId);
      specialtyGroupCodes.push(state.regionSetSpecialtyGroupToPartsLookup[sr].specialtyGroupCode);
    });

    const selectedMemberships: number[] = [state.runConfiguration.memberSetId];
    if (state.isDualMembershipMode) {
      selectedMemberships.push(state.dualMembershipRunConfiguration.memberSetId);
    }

    const { primaryRunConfiguration, secondaryRunConfiguration } = getPrimaryAndSecondaryRunConfigurations(state.project.projectRunConfigurations, state.runConfiguration.projectRunConfigurationId);

    return (
      <span className="bulk-action-group">
        <Link<Partial<ExportDataRouteState> & ReportCustomizationRouteState>
          to={{
            pathname: `/projects/${routeMatch.params.projectId}/adequacy/export/${state.runConfiguration?.projectRunConfigurationId}/${state.runConfiguration?.isLatestPublished}`,
            state: {
              activeModule: state.module,
              adequacyFilters: state.adequacyFilters,
              availableExportTypes: [ExportType.ZipClassAggregateResultsReport],
              comparedRunConfigurationId: state.compareToRunConfiguration?.projectRunConfigurationId,
              disableRegionFilters: false,
              disableSpecialtyGroupFilters: false,
              granularity: RegionGranularity.Zip,
              hasMarketProviders: !!state.runConfiguration.marketProviderSetId,
              hasProspectProviders: !!state.runConfiguration.prospectProviderSetId,
              initialNetworkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea,
              // values needed for export properties
              // networkTypes: state.runConfiguration && state.runConfiguration.networkTypes || undefined,
              // nextClosestProviders: showNextClosestProviders,
              perspective: ProjectPerspective.All,
              projectResourceId: state.project.resource.resourceId,
              projectResourceName: state.project.resource.name,
              projectURL: location.pathname + location.search,
              regionSetIds,
              showAdvancedComparison: state.project.showAdvancedComparison,
              // showAppendNextClosestProviders: showNextClosestProviders,
              showExpandableSpecialties: state.hasExpandableSpecialties,
              specialtyGroupCodes,

              // new reportcustomization
              /* oxlint-disable sort-keys */
              projectName: state.project.resource.name,
              isZipProject: true,
              networkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea,
              showClosestProviderToMemberReport: state.project.showClosestProviderReport,
              selectedRegionSetIds: regionSetIds,
              selectedSpecialties: specialtyGroupCodes,
              defaultReportType: ReportType.ZipClassAggregateResultsReport,
              primaryMembership: { alias: primaryRunConfiguration.memberSetAlias || primaryRunConfiguration.memberSetName, id: primaryRunConfiguration.memberSetId },
              secondaryMembership: { alias: secondaryRunConfiguration?.memberSetAlias || secondaryRunConfiguration?.memberSetName, id: secondaryRunConfiguration?.memberSetId },
              selectedMemberships,
              selectedTimeslice: state.runConfiguration.projectRunConfigurationId, // runConfigurationId
              isCompareMode: state.isCompareMode,
              selectedComparedTimeslice: !state.isDualMembershipMode ? state.compareToRunConfiguration?.projectRunConfigurationId : null, // compareToRunConfigurationId
              isViewingOpportunity: state.adequacyFilters.opportunity,
              showAddedProviders: !!state.compareToRunConfiguration || !!state.dualMembershipRunConfiguration,
              showMatchedProviders: !!state.compareToRunConfiguration || !!state.dualMembershipRunConfiguration,
              showRemovedProviders: !!state.compareToRunConfiguration || !!state.dualMembershipRunConfiguration,
              showPatternOfCare: false,
              zipProjectSearchParameters: projectContext.getSearchParametersFromFilterSelections(state.filterSelections),
              showRegionSummaryReport: false,
              /* oxlint-enable sort-keys */
            },
          }}
          data-testid="adequacy-view-by-all__create-report-button"
        >
          <i className="fa fa-cloud-download" /> Create Report
        </Link>
      </span>
    );
  }, [
    location.pathname,
    location.search,
    state.adequacyFilters,
    state.runConfiguration?.projectRunConfigurationId,
    state.project,
    state.selectedRows,
    state.regionSetSpecialtyGroupToPartsLookup,
    state.isDualMembershipMode,
    state.compareToRunConfiguration,
    state.isCompareMode,
    state.dualMembershipRunConfiguration,
    state.module,
    state.hasExpandableSpecialties,
    state.filterSelections
  ]);

  const rowDecorator = (row: ZipClassTableData) => {
    let classNames = row.metOverall === "Y" ? "success" : "danger";
    if (row.isInformationalSpecialty) {
      classNames += " muted";
    }
    if (row.membershipCount === "0" || row.membershipCount === "---") {
      classNames += " gray";
    }
    return classNames;
  }

  // records is always undefined here because of passThroughSorting
  const customSort = (records: ZipClassTableData[], column: keyof ZipClassTableData, direction: SortDirections) => {
    dispatch({
      payload: { loading: true },
      type: ZipProjectActionsEnum.Loading,
    });
    projectContext.onSortTable(column, direction);
    return records;
  }

  const buildZipClassTableData = (
    rows: ZipClassAggregateResultDisplayModel[],
    isExpansion?: boolean,
    timestamp?: string
  ): ZipClassTableData[] => {
    const hasInformationalSpecialties = (rows || []).some((r) => r.isInformationalSpecialty);
    return (rows || []).map((row) => {
      // order doesn't really matter here because of the sortOrder properties in columnConfig
      /* oxlint-disable sort-keys */
      const newRow: ZipClassTableData = {
        [wafflesKeyColumnName]: isExpansion ? row.zipClass + row.specialtyGroupCode + row.specialtyCode : row.zipClass + row.specialtyGroupCode,
        zipClass: row.zipClass,
        isInformationalSpecialty: row.isInformationalSpecialty,
        specialty: isExpansion ? `${row.specialtyCode}` : (
          <span className="d-inline-flex align-items-end">
            <InformationalIndicator visible={hasInformationalSpecialties} isInformational={row.isInformationalSpecialty} />
            <span>{row.specialtyGroupCode} - {row.specialtyGroupDescription}</span>
          </span>
        ),
        membershipType: row.membershipType,
        resultsFor: timestamp,
        reqDist: row.reqDist.toLocaleString(),
        reqTime: row.reqTime.toLocaleString(),
        minPassPercent: printPercentString(row.minPassPercent),
        membershipCount: row.membershipCount.toLocaleString(),
        minProviderCount: row.minProviderCount.toLocaleString(),
        accessTelehealthCredit: printNumericString(row.accessTelehealthCredit),
        providerCount: row.providerCount.toLocaleString(),
        memberCountWith: row.memberCountWithAccess.toLocaleString(),
        withPercent: printPercentString(row.withAccessPercent),
        memberCountWithout: row.memberCountWithoutAccess.toLocaleString(),
        withoutPercent: printPercentString(row.withoutAccessPercent),
        metDist: row.metDist ? "Y" : "N",
        metTime: row.metTime ? "Y" : "N",
        metOverall: row.metOverall ? "Y" : "N",
        avgDist: printNumericString(row.averageDistance),
        avgDistWith: printNumericString(row.averageDistanceWithAccess),
        avgDistWithout: printNumericString(row.averageDistanceWithoutAccess),
        exceptions: (row.exceptionCount || 0).toLocaleString(),
        maxDist: printNumericString(row.maxDistance),
        maxDistWith: printNumericString(row.maxDistanceWithAccess),
        maxDistWithout: printNumericString(row.maxDistanceWithoutAccess),
        withDistPercent: printPercentString(row.withDistPercent),
        avgTime: printNumericString(row.averageTime),
        avgTimeWith: printNumericString(row.averageTimeWithAccess),
        avgTimeWithout: printNumericString(row.averageTimeWithoutAccess),
        maxTime: printNumericString(row.maxTime),
        maxTimeWith: printNumericString(row.maxTimeWithAccess),
        maxTimeWithout: printNumericString(row.maxTimeWithoutAccess),
        withTimePercent: printNumericString(row.withTimePercent),
        avgDist2: printNumericString(row.averageDistance2),
        avgDistWith2: printNumericString(row.averageDistanceWithAccess2),
        avgDistWithout2: printNumericString(row.averageDistanceWithoutAccess2),
        maxDist2: printNumericString(row.maxDistance2),
        maxDistWith2: printNumericString(row.maxDistanceWithAccess2),
        maxDistWithout2: printNumericString(row.maxDistanceWithoutAccess2),
        avgTime2: printNumericString(row.averageTime2),
        avgTimeWith2: printNumericString(row.averageTimeWithAccess2),
        avgTimeWithout2: printNumericString(row.averageTimeWithoutAccess2),
        maxTime2: printNumericString(row.maxTime2),
        maxTimeWith2: printNumericString(row.maxTimeWithAccess2),
        maxTimeWithout2: printNumericString(row.maxTimeWithoutAccess2),
        marketAccessPercent: printPercentString(row.marketAccessPercent),
        marketPercent: printPercentString(row.marketPercent),
        marketServicing: row.marketServicing?.toLocaleString(),
        inClassProviderCount: row.inClassProviderCount?.toLocaleString() ?? "---",
        inClassMarketProviderCount: row.inClassMarketProviderCount?.toLocaleString() ?? "---",
        servicingProviderToMemberRatio: row.servicingProviderToMemberRatio || "---",
      };
      return newRow;
    });
  }

  const [tableData, tableDataCompared] = useMemo(() => {
    const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(state.runConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
    const topTimestamp = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING) || enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION) ? getResultsForLabel(topConfig) : formatDateTime(topConfig?.dateCreated);
    const bottomTimestamp = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING) || enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION) ? getResultsForLabel(bottomConfig) : formatDateTime(bottomConfig?.dateCreated);

    const usedDetails = shouldUseComparedZipDetails(state.isDualMembershipMode, state.isCompareMode, topConfig.projectRunConfigurationType)
      ? state.zipDetailsCompared
      : state.zipDetails;

    const newTableData = buildZipClassTableData(usedDetails as ZipClassAggregateResultDisplayModel[], false, topTimestamp);
    const newTableDataCompared = state.isCompareMode || state.isDualMembershipMode ? buildZipClassTableData(state.zipDetailsCompared as ZipClassAggregateResultDisplayModel[], false, bottomTimestamp) : undefined;

    const [completeNewTableData, completeNewTableDataCompared] = fillInMissingCompareModeRows(newTableData, newTableDataCompared, blankZipClassRow, topTimestamp, bottomTimestamp);
    return [completeNewTableData, completeNewTableDataCompared];
  }, [state.zipDetails, state.zipDetailsCompared, state.isCompareMode, state.isDualMembershipMode, state.runConfiguration]);

  const expansionData = useMemo(() => {
    const expansionData = new Map<string, ZipClassTableData[]>();
    const usedExpansions = shouldUseComparedZipDetails(state.isDualMembershipMode, state.isCompareMode, state.runConfiguration?.projectRunConfigurationType)
      ? state.zipExpansionsCompared
      : state.zipExpansions;
    usedExpansions.forEach((values, key) => {
      expansionData.set(key, buildZipClassTableData(values as ZipClassAggregateResultDisplayModel[], true));
    })
    return expansionData;
  }, [state.zipExpansions, state.zipExpansionsCompared, state.isDualMembershipMode, state.isCompareMode, state.runConfiguration]);

  const comparedExpansionData = useMemo(() => {
    if (!state.isCompareMode)
      return undefined;

    const expansionData = new Map<string, ZipClassTableData[]>();
    state.zipExpansionsCompared.forEach((values, key) => {
      expansionData.set(key, buildZipClassTableData(values as ZipClassAggregateResultDisplayModel[], true));
    })
    return expansionData;
  }, [state.zipExpansionsCompared, state.isCompareMode]);

  const columnGroups: ColumnGroup<ZipClassTableData>[] = useMemo(() => {
    return [
      {
        children: [
          "zipClass",
          "specialty",
          "resultsFor",
          "membershipType",
          "membershipCount",
        ],
        hideKey: true,
        key: "general",
        testId: "general",
      },
      {
        children: [
          "reqDist",
          "reqTime",
          "minPassPercent",
          "memberCountWith",
          "withPercent",
          "memberCountWithout",
          "withoutPercent",
          "accessTelehealthCredit",
          "providerCount",
          "minProviderCount",
          "metDist",
          "metTime",
          "metOverall",
          "avgDist",
          "avgDistWith",
          "avgDistWithout",
          "maxDist",
          "maxDistWith",
          "maxDistWithout",
          "withDistPercent",
          "avgTime",
          "avgTimeWith",
          "avgTimeWithout",
          "maxTime",
          "maxTimeWith",
          "maxTimeWithout",
          "withTimePercent",
          "avgDist2",
          "avgDistWith2",
          "avgDistWithout2",
          "maxDist2",
          "maxDistWith2",
          "maxDistWithout2",
          "avgTime2",
          "avgTimeWith2",
          "avgTimeWithout2",
          "maxTime2",
          "maxTimeWith2",
          "maxTimeWithout2",
          "inClassProviderCount",
          "servicingProviderToMemberRatio",
        ],
        key: "Access Requirements",
        testId: "access-requirements",
      },
      {
        children: ["marketAccessPercent", "marketServicing", "inClassMarketProviderCount", "marketPercent"],
        hideKey: !displayMarketAnalysisResults,
        key: "Market Analysis",
        showInfoIcon: true,
        testId: "market-analysis",
        tooltip: getMarketAnalysisTooltip(state.runConfiguration),
      },
      {
        children: ["exceptions"],
        hideKey: true,
        key: "Exceptions",
        testId: "exceptions",
      }
    ];
  }, [state.runConfiguration]);

  return (
    <Waffles<ZipClassTableData>
      key={String(state.sortResetCount)}
      bulkActionBar={bulkActionBar}
      columns={columnConfig}
      columnGroups={columnGroups}
      compareData={tableDataCompared}
      compareExpansions={comparedExpansionData}
      compareModeKeyColumns={2}
      customSort={customSort}
      data={tableData}
      dataExpansions={expansionData}
      enablePagination={true}
      fullWidth={true}
      handleCheckboxChange={handleCheckboxChange}
      loading={state.loading}
      paginationProps={paginationProps}
      passThroughSorting={true}
      rowDecorator={rowDecorator}
      selectedKeys={state.selectedRows}
      totalNumberOfRecords={calculatedTotalNumberOfRecords}
      testId="zip-class-table"
    />
  );
}